import { useMemo } from 'react'
import Icon from '@/components/Icon'
import Modal from '@/components/Modal'
import RetainQueryLinkDefault from '@/components/RetainQueryLink/RetainQueryLinkDefault'
import { useTonPlans } from '@/contexts/plans'
import { useReceivingOption } from '@/contexts/receivingOption'
import { TierFees } from '../TierFees'
import { useFlagGroup } from '@/contexts/flagsGroup'
import { sortPlansByLastFee } from '@/utils/sortPlansByLastFee'

interface DefaultVariantProps {
  isOpen: boolean
  onClose(): void
  groupTier: string
}

export const DefaultVariant = ({
  isOpen,
  onClose,
  groupTier
}: DefaultVariantProps) => {
  const { flagsGroup } = useFlagGroup()
  const { plansByGroupTier } = useTonPlans()
  const { receivingOptionText, receivingOption } = useReceivingOption()

  const tiers = useMemo(() => {
    return plansByGroupTier.get(groupTier) || []
  }, [groupTier, plansByGroupTier])

  const planTiers = useMemo(() => {
    return tiers.sort(sortPlansByLastFee(receivingOption, flagsGroup))
  }, [flagsGroup, tiers, receivingOption])

  const defaultPlanTier = planTiers.find((plan) => plan.isDefaultTier)

  if (!defaultPlanTier) return null

  return (
    <Modal
      open={isOpen}
      closeOnClickOut={true}
      onCloseClick={onClose}
      className="flex items-end md:items-center !p-0 w-full md:max-w-[618px] h-full bg-transparent rounded-b-none"
      classNameContainer="z-[101]"
    >
      {tiers.length > 1 && (
        <div className="flex overflow-y-auto relative flex-col gap-16 p-24 w-full max-h-[90%] md:max-h-full bg-white md:rounded-[16px] rounded-t-24 rounded-b-none">
          <div className="flex gap-24 items-start">
            <p className="font-bold leading-5 text-display-900 ton-paragraph-20">
              Como funciona o plano {defaultPlanTier.commercial_name}?
            </p>

            <button className="ml-auto cursor-pointer" onClick={onClose}>
              <Icon name="close" className="ml-auto w-24 h-24 fill-current" />
            </button>
          </div>
          <div className="flex flex-col gap-24 text-center text-display-900 ton-paragraph-16">
            {/* TODO: refactor these paragraphs to get the text from the CMS */}
            {defaultPlanTier.type === 'tap_phone' && (
              <p>
                O primeiro mês terá taxas relativas a vendas mensais até R$ 5
                mil, após esse período, você terá taxas de acordo com o valor de
                vendas.
              </p>
            )}

            {defaultPlanTier.type !== 'tap_phone' && (
              <p>
                O primeiro mês terá taxas relativas a vendas mensais de R$ 2 mil
                a R$ 20 mil, após esse período, você terá taxas de acordo com o
                valor de vendas.
              </p>
            )}

            <p>
              No início de cada mês, são contabilizadas as suas vendas do mês
              anterior. Isso definirá a sua faixa de vendas e, consequentemente,
              as taxas do mês.
              {defaultPlanTier.type === 'tap_phone' && (
                <>
                  {' '}
                  Escolha o recebimento de venda no app do Ton após fazer o
                  cadastro.
                </>
              )}
            </p>
          </div>

          <div>
            <p className="mb-8 text-center text-display-900 ton-paragraph-14">
              Taxas válidas para o recebimento da venda: {receivingOptionText}
            </p>

            <div className="grid gap-16 md:gap-8">
              {planTiers.map((planTier) => (
                <TierFees key={planTier.name} planTier={planTier} />
              ))}
            </div>
          </div>
        </div>
      )}

      {tiers.length === 1 && (
        <div className="flex overflow-y-auto relative flex-col gap-16 p-24 w-full max-h-[90%] md:max-h-full bg-white md:rounded-[16px] rounded-t-24 rounded-b-none">
          <div className="flex gap-24 items-start">
            <p className="font-bold leading-5 text-display-900 ton-paragraph-20">
              Como funciona o plano {defaultPlanTier.commercial_name}?
            </p>

            <button className="ml-auto cursor-pointer" onClick={onClose}>
              <Icon name="close" className="ml-auto w-24 h-24 fill-current" />
            </button>
          </div>

          <div className="flex flex-col gap-24 text-center md:text-left text-display-900 ton-paragraph-16">
            <p>
              No plano {defaultPlanTier.commercial_name}, as maquininhas têm
              preços mais baratos e as taxas não mudam de acordo com as suas
              vendas mensais.
            </p>
            <p>
              As taxas se aplicam a vendas no link de pagamento e a todas as
              vendas na maquininha, exceto Pix QR Code.
            </p>

            <p className="ton-paragraph-16">
              Veja os{' '}
              <RetainQueryLinkDefault
                target="_blank"
                href="https://documentos.ton.com.br/termos-e-condicoes.pdf"
              >
                <span className="text-newTon-700 underline">
                  termos e condições
                </span>
              </RetainQueryLinkDefault>
              .
            </p>
          </div>
        </div>
      )}
    </Modal>
  )
}
